import "./App.css";
import "./montserrat.css";
import "./css/transitions.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from "framer-motion";

import Home, { preloadHomeData } from "./website/home";
import { preloadCriticalAssets } from "./utils/preload";
import { debugPreloadState } from "./utils/debug";
import { initializeNavigationTracking, applyInstantBikeCategoryImages } from "./utils/imageTransitionUtils";
import Error from "./website/error";

import Ridley from "./website/o-ridley";
import Prodejci from "./website/prodejci";
import Novinky from "./website/novinky";

import SeznamKol from "./website/seznamkol";
import BikeExample from "./website/bike-example";
import ArticleExample from "./website/article-example";
import Configurator from "./website/configurator";
import ModalBox from "./components/modal";
import ModalTable from "./components/tableModal";

import Footer from "./components/footer";
import Loader from "./components/loader";
import Topbar from "./components/topbar";
import ScrollToTop from "./components/scrollup";
import { builder } from './utils/builderWithLocale';
import Kontakt from "./website/kontakt";

import { HelmetProvider } from "react-helmet-async";
import { GlobalSeo } from "./components/GlobalSeo";

import Pruvodce from "./website/pruvodce";
import Zaruka from "./website/zaruka";
import Login from "./website/login";

// Initialize Builder.io API key first, before any preloading happens
builder.init("425a1e92273943bab5fbaee91f6e709e");

// Store the API key in a global variable for debugging
window.builderAPIKey = "425a1e92273943bab5fbaee91f6e709e";

// Start preloading the home page data right away if we're on the home page
if (window.location.pathname === "/" || window.location.pathname === "") {
  try {
    // Initialize Builder.io API key first, before any preloading happens
    if (!builder.apiKey) {
      builder.init("425a1e92273943bab5fbaee91f6e709e");
      console.warn("Had to re-initialize Builder.io API key");
    }
    
    // Start preloading right away
    if (typeof preloadHomeData === 'function') {
      const preloadedData = preloadHomeData();
      
      // Load our enhanced caching system as early as possible
      import('./utils/globalImageCache').then(cacheModule => {
        // Make it globally available to ensure it's not garbage collected
        window.globalImageCache = cacheModule.default;
      }).catch(err => {
        console.error('Failed to import globalImageCache early:', err);
      });
      
      // Preload data and assets in parallel before React even mounts
      Promise.all([
        preloadedData.homeContent,
        preloadedData.novinkyContent,
        preloadedData.pick4Content,
        preloadedData.headerContent,
        preloadedData.footerContent,
        preloadCriticalAssets()
      ]).then(([homeData, novinkyData, pick4Data, headerData, footerData]) => {
        //console.log("Early preload completed successfully");
        
        window.preloadedHomeData = {
          homeData,
          novinkyData,
          pick4Data,
          headerData,
          footerData
        };
        
        // Preload bike images as early as possible
        if (pick4Data && pick4Data.length && window.globalImageCache) {
          //console.log('Preloading bike images with enhanced caching system');
          const bikeUrls = pick4Data
            .filter(item => item.data && item.data.obrazek)
            .map(item => item.data.obrazek);
            
          window.globalImageCache.preloadImages(bikeUrls);
        }
      }).catch(error => {
        console.error("Error in early preload:", error);
      });
    }
  } catch (error) {
    console.error("Error starting early preload:", error);
  }
}

// Builder.io is already initialized above, don't initialize it again
// builder.init("425a1e92273943bab5fbaee91f6e709e")

function App() {
  const location = useLocation();
  const [loader, setLoader] = useState(true);
  const [mode, setMode] = useState("dark");
  const [preloadComplete, setPreloadComplete] = useState(false);
  const [isHidingLoader, setIsHidingLoader] = useState(false);
  
  // Initialize navigation tracking on mount
  useEffect(() => {
    initializeNavigationTracking();
  }, []);

  // Force complete the loader after a maximum time to prevent infinite loading
  useEffect(() => {
    // Create a persistent flag to track navigation history
    if (typeof sessionStorage !== 'undefined') {
      const hasVisitedHome = sessionStorage.getItem('hasVisitedHome');
      const isReturningToHome = location.pathname === "/" && hasVisitedHome === 'true';
      
      // Always mark that we've visited home
      sessionStorage.setItem('hasVisitedHome', 'true');
      
      // If returning to home, ensure all images show instantly
      if (isReturningToHome) {
        //console.log('Returning to home page, applying cached images immediately');
        
        // Apply instant transitions to bike category images
        applyInstantBikeCategoryImages();
        
        // Import our enhanced image cache utilities and apply them
        import('./utils/globalImageCache').then(cacheModule => {
          // Apply cached images to all bike elements
          const elements = document.querySelectorAll('.option2');
          elements.forEach(el => {
            const url = el.dataset.imageSrc;
            if (url) {
              cacheModule.setBackgroundFromCache(el.id, url);
            }
          });
          
          // Also start preloading all bike images in the background for future use
          setTimeout(() => {
            cacheModule.preloadAllBikeCategoryImages();
          }, 100);
        });
      }
    }

    const forceCompleteTimeout = setTimeout(() => {
      if (loader) {
        console.warn("Force completing loader after timeout");
        if (typeof debugPreloadState === 'function') {
          debugPreloadState(); // Log debug info before force completion
        }
        setPreloadComplete(true);
      }
    }, 2000); // Force complete after 2 seconds max
    
    return () => clearTimeout(forceCompleteTimeout);
  }, [loader, location.pathname]);

  // Check if preloading is needed or already complete
  useEffect(() => {
    // Only additional preload for home page
    if (location.pathname === "/" || location.pathname === "") {
      //console.log("Checking if preloading is needed or already complete");
      
      // Apply cached images efficiently without re-fetching
      // This ensures immediate display when returning to the home page
      const applyCachedImages = () => {
        if (window.preloadedHomeData) {
          // Import our enhanced image cache utilities
          import('./utils/globalImageCache').then(cache => {
            // Apply all cached images immediately
            setTimeout(() => {
              //console.log('Applying cached bike images from persistent storage');
              cache.default.preloadAllBikeCategoryImages();
            }, 100);
          }).catch(err => {
            console.error('Failed to import image cache module:', err);
          });
        }
      };
      
      // Add listener for navigation events
      window.addEventListener('popstate', applyCachedImages);
      
      // If early preload was successful and data is already available
      if (window.preloadedHomeData) {
        //console.log("Using data from early preload");
        setPreloadComplete(true);
        applyCachedImages(); // Apply cached images without re-fetching
        return () => {
          window.removeEventListener('popstate', applyCachedImages);
        };
      }
      
      // Otherwise start preloading now
      try {
        // Start preloading both data and assets in parallel
        const preloadPromises = [];
        
        // Preload API data if possible
        if (typeof preloadHomeData === 'function') {
          const preloadedData = preloadHomeData();
          
          // API data preloading
          const dataPromise = Promise.all([
            preloadedData.homeContent,
            preloadedData.novinkyContent,
            preloadedData.pick4Content,
            preloadedData.headerContent,
            preloadedData.footerContent
          ]).then(([homeData, novinkyData, pick4Data, headerData, footerData]) => {
            //console.log("API data preloaded successfully");
            // Store the preloaded data in the window object
            window.preloadedHomeData = {
              homeData,
              novinkyData,
              pick4Data,
              headerData,
              footerData
            };
            return true; // Return true to indicate successful data loading
          }).catch(error => {
            console.error("Error preloading API data:", error);
            return false; // Return false to indicate failed data loading
          });
          
          preloadPromises.push(dataPromise);
        }
        
        // Preload critical assets (images)
        const assetsPromise = preloadCriticalAssets()
          .then(() => {
            //console.log("Critical assets preloaded successfully");
            return true; // Return true to indicate successful asset loading
          })
          .catch(error => {
            console.error("Error preloading assets:", error);
            return false; // Return false to indicate failed asset loading
          });
        
        preloadPromises.push(assetsPromise);
        
        // Wait for all preloading to complete without enforcing a minimum display time
        Promise.all(preloadPromises)
          .then(results => {
            //console.log("All preloading completed, results:", results);
            
            // Make sure data is actually stored before transitioning
            if (window.preloadedHomeData) {
              // Complete immediately once data is loaded
              setPreloadComplete(true);
            } else {
              // If data isn't stored yet, wait a bit longer
              //console.log("Data not properly stored yet, waiting additional time");
              setTimeout(() => {
                if (window.preloadedHomeData) {
                  setPreloadComplete(true);
                } else {
                  console.error("Data still not available after delay, proceeding anyway");
                  setPreloadComplete(true);
                }
              }, 100); // Reduced from 300ms to 100ms
            }
          })
          .catch(error => {
            console.error("Error during preloading:", error);
            // Proceed immediately even on error
            setPreloadComplete(true);
          });
      } catch (error) {
        console.error("Error in preload process:", error);
        // No delay on error
        setPreloadComplete(true);
      }
    } else {
      // For non-home pages, no delay
      setPreloadComplete(true);
    }
  }, [location.pathname]);

  // Hide loader when preloading is complete
  useEffect(() => {
    if (preloadComplete) {
      //console.log("Preload complete, starting fade-out transition");
      // Start the fade transition
      setIsHidingLoader(true);
      
      // Only remove the loader component from DOM after the transition completes
      setTimeout(() => {
        //console.log("Removing loader from DOM after transition");
        setLoader(false);
      }, 600); // Match duration with CSS transition
    }
  }, [preloadComplete]);

  useEffect(() => {
    if (mode === "light") {
      document.body.style.backgroundColor = 'rgb(255, 255, 255)';
    } else {
      document.body.style.backgroundColor = 'rgb(255, 255, 255)';
    }
  }, [mode]);

  const [modal, setModal] = useState(false)
  const [tableModal, setTableModal] = useState({ show: false, id: null })

  const [titleModal, setTitleModal] = useState("null")
  const [textModal, setTextModal] = useState("null")
  const [imageModal, setImageModal] = useState("null")


  const [bikeData, setBikeData] = useState([]);

  useEffect(() => {
    const fetchBikeData = async () => {
      try {
        const data = await builder.getAll("kola", { limit: 200 });
        
        if (!data || !Array.isArray(data)) {
          console.error("Data nejsou ve správném formátu:", data);
          return;
        }

        if (data) {
          setBikeData(data);
        }
      } catch (error) {
        console.error("Chyba při načítání dat:", error);
      }
    };


    if (window.location.pathname === "/") {
      setTimeout(() => {
        fetchBikeData();
      }, 5000);
    } else {
      fetchBikeData();
    }
  }, []);



  return (
    <div className="light">
      <HelmetProvider>
        <GlobalSeo />
      <ToastContainer theme="light" position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick draggable pauseOnHover />
      <div className="background overlay-transition-container">
        {/* Both content and loader rendered simultaneously */}
        <div className="site-content" style={{ 
          opacity: loader ? 0 : 1,
          transition: "opacity 0.3s ease-in",
          transitionDelay: "0.3s",
          position: "relative",
          zIndex: 1
        }}>
          <div className="topbar-container">
            <Topbar mode="light"/>
          </div>
          
          <div className="content-container">
            <ScrollToTop/>

            <AnimatePresence>
              {modal && 
              <ModalBox imageModal={imageModal} setModal={setModal} titleModal={titleModal} textModal={textModal}/>
              }
              {tableModal.show && 
              <ModalTable setModal={setTableModal} tableId={tableModal.id} />
              }
            </AnimatePresence>

            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/o-ridley" element={<Ridley />} />
              <Route path="/prodejci" element={<Prodejci />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/kola/:kategorie" element={<SeznamKol bikeData={bikeData} />} />
              <Route path="/articles" element={<Novinky />} />
              <Route path="/konfigurator" element={<Configurator />} />

              <Route path="/pruvodce/:category" element={<Pruvodce />} />
              <Route path="/kola/:kategorie/:id" element={<BikeExample bikeData={bikeData} theme={mode} setTableModal={setTableModal} setModal={setModal} setTitleModal={setTitleModal} setTextModal={setTextModal} setImageModal={setImageModal}/>} />
              <Route path="/article/:id" element={<ArticleExample />} />
              {/* <Route path="/prodejce/:id" element={<ProdejciExample />} /> */}
              {/* <Route path="/prodejce/:id/novinky/:novinkaid" element={<ArticleProdejci />} /> */}

              <Route path="/prodlouzena-zaruka" element={<Zaruka />} />
              <Route path="/login" element={<Login />} />

              <Route path="*" element={<Error />} />
            </Routes>
          </div>
          
          <div className="content-container z-index1">
            <Footer setMode={setMode} mode="light"/>
          </div>
        </div>

        {/* Loader on top */}
        {loader && (
          <Loader isHiding={isHidingLoader} />
        )}
      </div>
      </HelmetProvider>
    </div>
  );
}

export default App;
